import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import QuizFormDialog from '../../../components/quiz/QuizFormDialog';
import QuestionFormDialog from '../../../components/quiz/QuestionFormDialog';
import QuizHeader from '../../../components/quiz/builder/QuizHeader';
import QuizMetadata from '../../../components/quiz/builder/QuizMetadata';
import QuestionList from '../../../components/quiz/builder/QuestionList';
import { QuizBuilderProvider, useQuizBuilder } from '../../../contexts/QuizBuilderContext';
import {
  UpdateQuizData,
  CreateQuizData,
  CreateQuizQuestionData,
  UpdateQuizQuestionData,
  QuizQuestion,
} from '../../../types/quiz';

/**
 * Inner component that uses the QuizBuilder context
 * Handles all quiz building operations with extracted components
 */
function QuizBuilderContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewQuiz = !id || id === 'new';
  const quizId = isNewQuiz ? null : Number(id);

  const {
    quiz,
    loading,
    error,
    successMessage,
    loadQuiz,
    createNewQuiz,
    updateQuizDetails,
    togglePublishStatus,
    addQuestionToQuiz,
    updateQuestionInQuiz,
    deleteQuestionFromQuiz,
    clearError,
    clearSuccessMessage,
  } = useQuizBuilder();

  const [quizFormOpen, setQuizFormOpen] = useState(isNewQuiz);
  const [questionFormOpen, setQuestionFormOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<QuizQuestion | null>(null);

  useEffect(() => {
    if (!isNewQuiz && quizId) {
      loadQuiz(quizId);
    }
  }, [quizId, isNewQuiz, loadQuiz]);

  const handleCreateQuiz = async (data: CreateQuizData | UpdateQuizData) => {
    try {
      const newQuiz = await createNewQuiz(data as CreateQuizData);
      setQuizFormOpen(false);
      navigate(`/admin/quizzes/${newQuiz.id}/edit`, { replace: true });
    } catch (err) {
      // Error already handled by context
    }
  };

  const handleUpdateQuiz = async (data: CreateQuizData | UpdateQuizData) => {
    if (!quizId) return;

    try {
      await updateQuizDetails(quizId, data as UpdateQuizData);
      setQuizFormOpen(false);
    } catch (err) {
      // Error already handled by context
    }
  };

  const handleTogglePublish = async () => {
    if (!quiz) return;

    try {
      await togglePublishStatus(quiz);
    } catch (err) {
      // Error already handled by context
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion(undefined);
    setQuestionFormOpen(true);
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setEditingQuestion(question);
    setQuestionFormOpen(true);
  };

  const handleQuestionSubmit = async (data: CreateQuizQuestionData | UpdateQuizQuestionData) => {
    if (!quizId) return;

    try {
      if (editingQuestion) {
        await updateQuestionInQuiz(quizId, editingQuestion.id, data as UpdateQuizQuestionData);
      } else {
        await addQuestionToQuiz(quizId, data as CreateQuizQuestionData);
      }
      setQuestionFormOpen(false);
      setEditingQuestion(undefined);
    } catch (err) {
      // Error already handled by context
    }
  };

  const handleDeleteQuestionClick = (question: QuizQuestion) => {
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!quizId || !questionToDelete) return;

    try {
      await deleteQuestionFromQuiz(quizId, questionToDelete.id);
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
    } catch (err) {
      // Error already handled by context
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setQuestionToDelete(null);
  };

  const handleBack = () => {
    navigate('/admin/quizzes');
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error && !quiz) {
    return (
      <AdminLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="contained"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            Back to Quizzes
          </Button>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <QuizHeader
          quiz={quiz}
          onBack={handleBack}
          onEditDetails={() => setQuizFormOpen(true)}
          onTogglePublish={handleTogglePublish}
        />

        {/* Success/Error Messages */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={clearSuccessMessage}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {quiz && (
          <>
            {/* Quiz Metadata */}
            <QuizMetadata quiz={quiz} />

            <Divider sx={{ my: 4 }} />

            {/* Questions Section */}
            <QuestionList
              questions={quiz.questions || []}
              onAddQuestion={handleAddQuestion}
              onEditQuestion={handleEditQuestion}
              onDeleteQuestion={handleDeleteQuestionClick}
              isPublished={quiz.isPublished}
            />
          </>
        )}

        {/* Quiz Form Dialog */}
        <QuizFormDialog
          open={quizFormOpen}
          onClose={() => {
            setQuizFormOpen(false);
            if (isNewQuiz) {
              navigate('/admin/quizzes');
            }
          }}
          onSubmit={isNewQuiz ? handleCreateQuiz : handleUpdateQuiz}
          initialData={quiz || undefined}
        />

        {/* Question Form Dialog */}
        {quiz && (
          <QuestionFormDialog
            open={questionFormOpen}
            onClose={() => {
              setQuestionFormOpen(false);
              setEditingQuestion(undefined);
            }}
            onSubmit={handleQuestionSubmit}
            quizId={quiz.id}
            questionCount={quiz.questions?.length || 0}
            initialData={editingQuestion}
            isPublished={quiz.isPublished}
            defaultTimeLimitSeconds={quiz.defaultQuestionTimeLimit}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Delete Question?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this question? This action cannot be undone.
            </DialogContentText>
            {questionToDelete && (
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" fontWeight="medium">
                  {questionToDelete.questionText}
                </Typography>
              </Paper>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AdminLayout>
  );
}

/**
 * Quiz Builder Page
 * Allows admins to create and edit quizzes with questions
 * Now refactored with context and extracted components for better maintainability
 */
function QuizBuilderPage() {
  return (
    <QuizBuilderProvider>
      <QuizBuilderContent />
    </QuizBuilderProvider>
  );
}

export default QuizBuilderPage;
